import produce from "immer";
import React, { JSX } from "react";
import { Typography, Box, Checkbox, TextField, Button, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { QUESTIONNAIRE } from "../../../Assessment/Questionnaire/QuestionnaireRow";
import { QuestionnaireSettings as IQuestionnaireSettings } from "../../../../interfaces/ResourceCentreInterface";
import { ORDER_ACTION, OrderActionType, OrderButtons } from "./AssessmentLabels";

interface PropType {
  questionnaireSettings: IQuestionnaireSettings;
  setQuestionnaireSettings: React.Dispatch<React.SetStateAction<IQuestionnaireSettings>>;
  questionnairesOrder: Array<string>;
  setQuestionnairesOrder: React.Dispatch<React.SetStateAction<Array<string>>>;
}

const QuestionnaireSettings = ({
  questionnaireSettings,
  setQuestionnaireSettings,
  questionnairesOrder,
  setQuestionnairesOrder
}: PropType): JSX.Element => {
  const [newQuestion, setNewQuestion] = React.useState("");

  const handleAddNewQuestion = () => {
    if (!newQuestion.trim()) return;
    if (questionnairesOrder.includes(newQuestion.trim())) return;

    setQuestionnairesOrder([...questionnairesOrder, newQuestion.trim()]);
    setQuestionnaireSettings({
      ...questionnaireSettings,
      [newQuestion.trim()]: true
    });
    setNewQuestion("");
  };

  const handleDeleteQuestion = (label: string) => {
    setQuestionnairesOrder(questionnairesOrder.filter((q) => q !== label));
    const newSettings = { ...questionnaireSettings };
    delete newSettings[label];
    setQuestionnaireSettings(newSettings);
  };

  const handleOrdering = ({ action, label }: { action: OrderActionType; label: string }) => {
    const currentIndex = questionnairesOrder.indexOf(label);
    let updatedIndex: number = -1;
    if (action === ORDER_ACTION.UP) {
      updatedIndex = currentIndex - 1;
    } else if (action === ORDER_ACTION.DOWN) {
      updatedIndex = currentIndex + 1;
    }
    if (updatedIndex >= 0 && updatedIndex < questionnairesOrder.length) {
      setQuestionnairesOrder(() =>
        produce(questionnairesOrder, (draft) => {
          draft.splice(currentIndex, 1);
          draft.splice(updatedIndex, 0, label);
        })
      );
    }
  };

  React.useEffect(() => {
    if (!questionnairesOrder || questionnairesOrder.length === 0) {
      setQuestionnairesOrder(QUESTIONNAIRE.map((q) => q.dbKey));
    }
  }, [questionnairesOrder, setQuestionnairesOrder]);

  return (
    <Box ml={2}>
      <Box display="flex" mb={2} gap={2} alignItems="center">
        <TextField
          size="small"
          placeholder="Add new question"
          value={newQuestion}
          onChange={(e) => setNewQuestion(e.target.value)}
          variant="outlined"
          sx={{ flex: 1 }}
        />
        <Button variant="contained" onClick={handleAddNewQuestion} disabled={!newQuestion.trim()}>
          Add
        </Button>
      </Box>

      <Box display="flex" mb={2} gap={2}>
        <Typography width="260px" variant="body1" fontWeight="bold">
          Questionnaire
        </Typography>
        <Typography width="100px" variant="body1" fontWeight="bold">
          Ordering
        </Typography>
      </Box>
      <Box sx={{ height: "30rem", overflowY: "scroll", maxWidth: "41rem" }}>
        {questionnairesOrder.map((qKey, index) => (
          <Box display="flex" mb={2} gap={2} key={qKey}>
            <>
              <Checkbox
                checked={Boolean(questionnaireSettings?.[qKey] ?? true)}
                onChange={() => {
                  setQuestionnaireSettings({
                    ...questionnaireSettings,
                    [qKey]: questionnaireSettings?.[qKey] === false
                  });
                }}
              />
              <Typography width="260px" variant="body1" fontWeight="bold">
                {QUESTIONNAIRE.find(({ dbKey }) => dbKey === qKey)?.label || qKey}
              </Typography>
            </>
            <Typography
              width="100px"
              variant="body1"
              fontWeight="bold"
              display="flex"
              alignItems="center"
            >
              <OrderButtons
                isDisabled={{ up: index < 1, down: index > questionnairesOrder.length - 2 }}
                onClick={{
                  up: () => {
                    handleOrdering({
                      action: ORDER_ACTION.UP,
                      label: qKey
                    });
                  },
                  down: () => {
                    handleOrdering({
                      action: ORDER_ACTION.DOWN,
                      label: qKey
                    });
                  }
                }}
              />
              {!QUESTIONNAIRE.some((q) => q.dbKey === qKey) && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteQuestion(qKey)}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default QuestionnaireSettings;
